import { Rocket } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { HowItWorksStepArt } from "./how-it-works/HowItWorksStepArt";
import { LandingEndCta } from "./LandingEndCta";

const steps = [
  {
    id: "signup" as const,
    step: "PASSO 1",
    title: "Crie sua conta",
    description:
      "Cadastro rápido e escolha do plano. Em poucos minutos você já está pronto para configurar sua operação.",
    time: "1 min",
  },
  {
    id: "configure" as const,
    step: "PASSO 2",
    title: "Configure seu agente",
    description:
      "Escolha o tom de voz, defina horários e conecte o WhatsApp. Tudo visual, sem código.",
    time: "3 min",
  },
  {
    id: "sell" as const,
    step: "PASSO 3",
    title: "Comece a vender",
    description:
      "Seu agente humanizado atende, qualifica leads e agenda reuniões enquanto você foca no fechamento.",
    time: "1 min",
  },
];

export const HowItWorks = () => {
  const { ref, isInView } = useInView({ threshold: 0.08 });

  return (
    <section
      id="como-funciona"
      className="relative overflow-hidden border-t border-border/40 bg-background py-20 scroll-mt-24 md:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_20%_20%,hsl(var(--primary)/0.06),transparent_50%),radial-gradient(ellipse_60%_40%_at_80%_80%,rgba(251,191,36,0.08),transparent_55%)]"
        aria-hidden
      />

      <div className="container relative mx-auto max-w-6xl px-4">
        <div
          ref={ref}
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center transition-all duration-700 md:mb-20",
            isInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Configure em <span className="text-primary">5 minutos</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            Sem código, sem complexidade. Três passos simples e você já está vendendo 24/7.
          </p>
        </div>

        <div className="space-y-20 md:space-y-28">
          {steps.map((item, index) => {
            const reverse = index % 2 === 1;
            return (
              <div
                key={item.id}
                className={cn(
                  "grid items-center gap-10 transition-all duration-700 lg:grid-cols-2 lg:gap-16",
                  isInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
                )}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <div
                  className={cn(
                    "max-w-lg",
                    reverse ? "lg:col-start-2 lg:row-start-1" : "lg:pr-4",
                  )}
                >
                  <span className="inline-block rounded-full border border-border/80 bg-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {item.step}
                  </span>
                  <h3 className="mt-4 text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-4xl">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
                    {item.description}
                  </p>
                  <span className="mt-4 inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    {item.time}
                  </span>
                </div>

                <div
                  className={cn(
                    "flex justify-center lg:justify-end",
                    reverse && "lg:col-start-1 lg:row-start-1 lg:justify-start",
                  )}
                >
                  <HowItWorksStepArt step={item.id} />
                </div>
              </div>
            );
          })}
        </div>

        <div
          className={cn(
            "mt-16 flex justify-center transition-all duration-700 md:mt-20",
            isInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
          style={{ transitionDelay: "400ms" }}
        >
          <div className="inline-flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-8 py-4 shadow-sm">
            <Rocket className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="text-left">
              <p className="font-bold text-foreground">Total: 5 minutos</p>
              <p className="text-sm text-muted-foreground">Do zero ao primeiro atendimento automatizado</p>
            </div>
          </div>
        </div>

        <LandingEndCta />
      </div>
    </section>
  );
};
